import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Badge,
  Tabs,
  Tab,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { api } from '../services/api';

interface ReviewItem {
  id: string;
  type: string;
  transactionId?: string;
  allocationId?: string;
  description: string;
  payeeName?: string;
  normalizedPayee?: string;
  selitys?: string;
  amountEur?: number;
  resolved: boolean;
  createdAt: string;
  month: string;
}

interface Category {
  id: string;
  name: string;
  parentId?: string;
  type: string;
}

/** Group uncategorized items by normalizedPayee */
interface PayeeGroup {
  normalizedPayee: string;
  displayName: string;
  count: number;
  totalAmount: number;
  items: ReviewItem[];
}

export default function ReviewPage() {
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0); // 0 = uncategorized, 1 = other, 2 = resolved

  // Per-group category selection
  const [selectedCats, setSelectedCats] = useState<Record<string, string>>({});
  // Per-individual item category selection (for non-grouped items)
  const [itemCats, setItemCats] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [ruleCreating, setRuleCreating] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    try {
      const [reviewItems, cats] = await Promise.all([
        api.get<ReviewItem[]>('/api/review-items'),
        api.get<Category[]>('/api/categories'),
      ]);
      setItems(reviewItems);
      setCategories(cats);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Category helpers
  const categoryLabel = useCallback(
    (catId: string) => {
      const cat = categories.find((c) => c.id === catId);
      if (!cat) return catId;
      if (cat.parentId) {
        const parent = categories.find((c) => c.id === cat.parentId);
        return parent ? `${parent.name} > ${cat.name}` : cat.name;
      }
      return cat.name;
    },
    [categories],
  );

  const selectableCategories = useMemo(() => {
    const parentIds = new Set(categories.filter((c) => c.parentId).map((c) => c.parentId));
    return categories.filter((c) => !parentIds.has(c.id) && c.id !== 'cat-uncategorized');
  }, [categories]);

  // Split items into groups
  const unresolvedUncategorized = useMemo(
    () => items.filter((i) => !i.resolved && i.type === 'uncategorized'),
    [items],
  );
  const unresolvedOther = useMemo(
    () => items.filter((i) => !i.resolved && i.type !== 'uncategorized'),
    [items],
  );
  const resolvedItems = useMemo(() => items.filter((i) => i.resolved), [items]);

  // Group uncategorized by payee
  const payeeGroups = useMemo((): PayeeGroup[] => {
    const map = new Map<string, PayeeGroup>();
    for (const item of unresolvedUncategorized) {
      const key = item.normalizedPayee || item.payeeName || item.id;
      if (!map.has(key)) {
        map.set(key, {
          normalizedPayee: key,
          displayName: item.payeeName || key,
          count: 0,
          totalAmount: 0,
          items: [],
        });
      }
      const group = map.get(key)!;
      group.count++;
      group.totalAmount += item.amountEur ?? 0;
      group.items.push(item);
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [unresolvedUncategorized]);

  // Set category for a single review item (no rule)
  const handleSetCategory = useCallback(
    async (itemId: string, categoryId: string) => {
      setSaving((p) => ({ ...p, [itemId]: true }));
      try {
        await api.patch('/api/review-items/' + itemId, { categoryId });
        setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, resolved: true } : i)));
      } catch (err: any) {
        setError(err.message);
      } finally {
        setSaving((p) => ({ ...p, [itemId]: false }));
      }
    },
    [],
  );

  // Create rule + recategorize for a payee group
  const handleCreateRuleAndCategorize = useCallback(
    async (group: PayeeGroup) => {
      const categoryId = selectedCats[group.normalizedPayee];
      if (!categoryId) return;

      setRuleCreating((p) => ({ ...p, [group.normalizedPayee]: true }));
      try {
        // 1. Create the rule
        await api.post('/api/rules', {
          pattern: group.normalizedPayee,
          patternType: 'exact',
          action: 'suggest_category',
          categoryId,
          priority: 100,
          enabled: true,
        });

        // 2. Recategorize allocations (also marks review items resolved)
        await api.post('/api/recategorize');

        // 3. Mark all items in this group as resolved locally
        const groupIds = new Set(group.items.map((i) => i.id));
        setItems((prev) =>
          prev.map((i) => (groupIds.has(i.id) ? { ...i, resolved: true } : i)),
        );
      } catch (err: any) {
        setError(err.message);
      } finally {
        setRuleCreating((p) => ({ ...p, [group.normalizedPayee]: false }));
      }
    },
    [selectedCats],
  );

  // Categorize a single item without creating a rule
  const handleCategorizeItem = useCallback(
    async (item: ReviewItem) => {
      const categoryId = itemCats[item.id];
      if (!categoryId) return;
      await handleSetCategory(item.id, categoryId);
    },
    [itemCats, handleSetCategory],
  );

  // Categorize all items in a group without creating a rule
  const handleCategorizeGroupWithoutRule = useCallback(
    async (group: PayeeGroup) => {
      const categoryId = selectedCats[group.normalizedPayee];
      if (!categoryId) return;

      setRuleCreating((p) => ({ ...p, [group.normalizedPayee]: true }));
      try {
        // Categorize each item in parallel
        await Promise.all(
          group.items.map((item) => api.patch('/api/review-items/' + item.id, { categoryId }))
        );
        
        // Mark all items in this group as resolved locally
        const groupIds = new Set(group.items.map((i) => i.id));
        setItems((prev) =>
          prev.map((i) => (groupIds.has(i.id) ? { ...i, resolved: true } : i)),
        );
      } catch (err: any) {
        setError(err.message);
      } finally {
        setRuleCreating((p) => ({ ...p, [group.normalizedPayee]: false }));
      }
    },
    [selectedCats],
  );

  // Mark other review item types as resolved
  const handleResolve = useCallback(
    async (itemId: string) => {
      setSaving((p) => ({ ...p, [itemId]: true }));
      try {
        await api.patch('/api/review-items/' + itemId, { resolved: true });
        setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, resolved: true } : i)));
      } catch (err: any) {
        setError(err.message);
      } finally {
        setSaving((p) => ({ ...p, [itemId]: false }));
      }
    },
    [],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  const totalUnresolved = unresolvedUncategorized.length + unresolvedOther.length;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Tarkistettavat
        </Typography>
        {totalUnresolved > 0 && (
          <Chip label={`${totalUnresolved} avointa`} color="warning" size="small" />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {items.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              Ei tarkistettavia. Tuo OP CSV -tiedosto luodaksesi merkintöjä.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab
              label={
                <Badge badgeContent={unresolvedUncategorized.length} color="warning" max={999}>
                  <Box sx={{ pr: unresolvedUncategorized.length > 0 ? 2 : 0 }}>Luokittelematon</Box>
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={unresolvedOther.length} color="info" max={999}>
                  <Box sx={{ pr: unresolvedOther.length > 0 ? 2 : 0 }}>Muut</Box>
                </Badge>
              }
            />
            <Tab label={`Käsitellyt (${resolvedItems.length})`} />
          </Tabs>

          {/* Tab 0: Uncategorized — grouped by payee */}
          {tab === 0 && (
            <>
              {payeeGroups.length === 0 ? (
                <Alert severity="success">
                  Ei luokittelemattomia tapahtumia!
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Valitse kategoria ja luo sääntö — sääntö luokittelee automaattisesti kaikki
                    nykyiset ja tulevat tapahtumat kyseiseltä maksajalta. Voit myös asettaa
                    kategorian yksittäiselle tapahtumalle ilman sääntöä.
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Maksaja</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Lopullinen saaja</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Kpl</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Yhteensä</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Kategoria</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="center"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payeeGroups.map((group) => (
                          <TableRow key={group.normalizedPayee}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {group.displayName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {group.items[0]?.month}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {group.items[0]?.selitys || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{group.count}</TableCell>
                            <TableCell align="right">
                              {group.totalAmount.toFixed(2)} €
                            </TableCell>
                            <TableCell sx={{ minWidth: 220 }}>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={selectedCats[group.normalizedPayee] || ''}
                                  displayEmpty
                                  onChange={(e) =>
                                    setSelectedCats((p) => ({
                                      ...p,
                                      [group.normalizedPayee]: e.target.value,
                                    }))
                                  }
                                >
                                  <MenuItem value="" disabled>
                                    <em>Valitse...</em>
                                  </MenuItem>
                                  {selectableCategories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                      {categoryLabel(cat.id)}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                              <Button
                                variant="contained"
                                size="small"
                                disabled={
                                  !selectedCats[group.normalizedPayee] ||
                                  ruleCreating[group.normalizedPayee]
                                }
                                onClick={() => handleCreateRuleAndCategorize(group)}
                                startIcon={
                                  ruleCreating[group.normalizedPayee] ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <AutoFixHighIcon />
                                  )
                                }
                                sx={{ mr: 1 }}
                              >
                                Luo sääntö
                              </Button>
                              {group.count === 1 ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  disabled={
                                    !selectedCats[group.normalizedPayee] ||
                                    saving[group.items[0].id]
                                  }
                                  onClick={() =>
                                    handleSetCategory(
                                      group.items[0].id,
                                      selectedCats[group.normalizedPayee],
                                    )
                                  }
                                >
                                  Vain tämä
                                </Button>
                              ) : (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  disabled={
                                    !selectedCats[group.normalizedPayee] ||
                                    ruleCreating[group.normalizedPayee]
                                  }
                                  onClick={() => handleCategorizeGroupWithoutRule(group)}
                                >
                                  Vain nämä
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}

          {/* Tab 1: Other review items (missing_card, needs_split, etc.) */}
          {tab === 1 && (
            <>
              {unresolvedOther.length === 0 ? (
                <Alert severity="success">Ei muita avoimia merkintöjä.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Tyyppi</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Kuvaus</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Kuukausi</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {unresolvedOther.map((item) => {
                        const chipColor =
                          item.type === 'missing_card'
                            ? 'info'
                            : item.type === 'needs_split'
                              ? 'secondary'
                              : 'default';
                        const chipLabel =
                          item.type === 'missing_card'
                            ? 'Puuttuva kortti'
                            : item.type === 'needs_split'
                              ? 'Jaettava'
                              : item.type === 'orphaned_allocation'
                                ? 'Orpo kohdistus'
                                : item.type;
                        return (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Chip label={chipLabel} size="small" color={chipColor as any} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.description}</Typography>
                            </TableCell>
                            <TableCell>{item.month}</TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={saving[item.id]}
                                onClick={() => handleResolve(item.id)}
                                startIcon={
                                  saving[item.id] ? <CircularProgress size={14} /> : <CheckCircleIcon />
                                }
                              >
                                Käsitelty
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Tab 2: Resolved items */}
          {tab === 2 && (
            <>
              {resolvedItems.length === 0 ? (
                <Alert severity="info">Ei käsiteltyjä merkintöjä.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Tyyppi</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Kuvaus</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Kuukausi</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resolvedItems.slice(0, 50).map((item) => (
                        <TableRow key={item.id} sx={{ opacity: 0.6 }}>
                          <TableCell>
                            <Chip label={item.type} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{item.description}</Typography>
                          </TableCell>
                          <TableCell>{item.month}</TableCell>
                        </TableRow>
                      ))}
                      {resolvedItems.length > 50 && (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography variant="body2" color="text.secondary">
                              ... ja {resolvedItems.length - 50} lisää
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
